import { useEffect, useRef, useState } from 'react';

import useOffers from 'api/useOffers';
import { Offer } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { useFormik } from 'formik';
import * as yup from 'yup';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';

interface BarPointsFormValues {
  progressBarId: string | null;
  progressBarPoints: number | null;
}

interface BarPointsFormProps {
  initialValues?: {
    progressBarId?: string | null;
    progressBarPoints?: number | null;
  };
  onChange: (
    values: BarPointsFormValues,
    isValid: boolean,
    isDirty: boolean
  ) => void;
}

const barPointsFormSchema = yup.object().shape({
  progressBarPoints: yup
    .number()
    .min(1, 'Progress bar points must be at least 1')
    .nullable()
    .test(
      'required-when-bar-id',
      'Progress bar points is required when Bar ID is set',
      function (value) {
        const { progressBarId } = this.parent;
        if (progressBarId && progressBarId !== '') {
          return value !== null && value !== undefined;
        }
        return true;
      }
    ),
  progressBarId: yup
    .string()
    .nullable()
    .test(
      'required-when-points',
      'Bar ID is required when Progress bar points is set',
      function (value) {
        const { progressBarPoints } = this.parent;
        if (progressBarPoints && progressBarPoints > 0) {
          return value !== null && value !== undefined && value !== '';
        }
        return true;
      }
    )
});

export const BarPointsForm: React.FC<BarPointsFormProps> = ({
  initialValues = {},
  onChange
}) => {
  const [progressBarOffers, setProgressBarOffers] = useState<Offer[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const { getOffers: getProgressBars } = useOffers({
    offerType: OfferType.PROGRESS_BAR
  });

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    validateForm,
    errors,
    isValid
  } = useFormik<BarPointsFormValues>({
    validateOnMount: true,
    validationSchema: barPointsFormSchema,
    enableReinitialize: true,
    initialValues: initialValues as BarPointsFormValues,
    onSubmit: () => {}
  });

  useEffect(() => {
    onChangeRef.current(values, isValid, isDirty);
  }, [values, isValid, isDirty]);

  useEffect(() => {
    if (!getProgressBars.data) return;
    const progressBarOffers = getProgressBars.data.offers;
    setProgressBarOffers(progressBarOffers);
  }, [getProgressBars.data]);

  return (
    <div className="grid grid-cols-2 gap-5">
      <InputSelectDropdown
        label="Bar ID"
        placeholder="Select Bar Name"
        disabled={progressBarOffers.length === 0}
        options={progressBarOffers.map((bar: Offer) => ({
          value: bar.offerId || '',
          label: bar.publisherOfferId || ''
        }))}
        defaultSelected={{
          value: values.progressBarId || '',
          label:
            progressBarOffers.find(
              (bar: Offer) => bar.offerId === values.progressBarId
            )?.publisherOfferId || ''
        }}
        onOptionChange={(value) => {
          setFieldValue('progressBarId', value);
          setIsDirty(true);
          validateForm();
        }}
        state={isDirty && errors.progressBarId ? 'error' : 'default'}
        onClear={() => {
          setIsDirty(true);
        }}
        description={
          progressBarOffers.length === 0
            ? 'There are no progress bars to select from'
            : isDirty && errors.progressBarId
              ? errors.progressBarId?.toString()
              : ''
        }
      />
      <Input
        label="Bar Points"
        name="progressBarPoints"
        value={values.progressBarPoints ?? ''}
        type="number"
        placeholder="0"
        disabled={progressBarOffers.length === 0}
        onChange={(e) => {
          handleChange(e);
          setIsDirty(true);
        }}
        onBlur={handleBlur}
        min={0}
        state={isDirty && errors.progressBarPoints ? 'error' : 'default'}
        description={isDirty ? errors.progressBarPoints?.toString() : ''}
      />
    </div>
  );
};

export default BarPointsForm;
